import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { ModalType, DeviceType, WorkType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useWorkListStore } from '../../shared/store/WorkListStore';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { getModalMessage } from '../../shared/constants/modalMessages';
import filterIcon from '../../asset/icon/filter.png';
import { useMemberCreditStore } from '../../shared/store/memberCreditStore';


const FilterStation: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { setStandardModal } = useModalStore();
  const filterData = useWorkListStore((s) => s.filterData);
  const setFilterData = useWorkListStore((s) => s.setFilterData);
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const { deviceType } = useDeviceStore();
  const credits = useMemberCreditStore((s) => s.credits);
  const isMobile = deviceType === DeviceType.MOBILE;
  // 모바일에서 activeWork가 있을 때 FilterStation을 숨기기 위한 플래그
  // WorkListMobile의 WorkListComp와 동일한 애니메이션 효과를 위해 사용
  const isActive = (isMobile && !!activeWork) || isEditMode;

  // workType 필터 표시 로직
  const getWorkTypeDisplayText = () => {
    const workTypeEntries = Object.entries(filterData.workType);
    const trueTypes = workTypeEntries.filter(([_, isSelected]) => isSelected);

    // 모든 타입이 true이면 "ALL" 표시
    if (trueTypes.length === workTypeEntries.length) {
      return "ALL";
    }

    const names = trueTypes.map(([type]) => type);
    if (names.length <= 2) {
      return names.join(", ");
    }
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  };

  const getMemberDisplayText = () => {
    const names = filterData.creditIds
      .map((id) => credits.find((credit) => credit.id === id)?.name)
      .filter((name): name is string => !!name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  const handleFilter = () => {
    setStandardModal({
      modalType: ModalType.FILTER_STATION,
      title: "Filter",
      value: "",
      handleChange: () => { },
    });
  }

  const removeFilterFactor = (factor: string) => {
    switch (factor) {
      case "prodYear":
        setFilterData({ ...filterData, prodYear: { min: null, max: null } });
        break;
      case "workType":
        const allTrueWorkType = Object.values(WorkType).reduce((acc, type) => {
          acc[type] = true;
          return acc;
        }, {} as { [key in WorkType]: boolean });
        setFilterData({ ...filterData, workType: allTrueWorkType });
        break;
      case "member":
        setFilterData({ ...filterData, creditIds: [] });
        break;
    }
  }

  const hasProdYearFilter = filterData.prodYear.min !== null || filterData.prodYear.max !== null;
  const hasWorkTypeFilter = Object.values(filterData.workType).some((isSelected) => !isSelected);
  const hasMemberFilter = filterData.creditIds.length > 0;
  const hasActiveFilters =
    hasMemberFilter ||
    hasProdYearFilter ||
    hasWorkTypeFilter;

  return (
    <FilterStationComp $isActive={isActive}>
      {!isMobile && (
      <FilterFactorContainer>
            {hasMemberFilter && (
              <FilterFactor onClick={() => removeFilterFactor("member")}>
                Member: {getMemberDisplayText()}
              </FilterFactor>
            )}
            {hasProdYearFilter && (
              <FilterFactor onClick={() => removeFilterFactor("prodYear")}>
                Year: {filterData?.prodYear?.min === null ? "0000" : filterData?.prodYear?.min} - {filterData?.prodYear?.max === null ? "NOW" : filterData?.prodYear?.max}
              </FilterFactor>
            )}
            {hasWorkTypeFilter && (
              <FilterFactor onClick={() => removeFilterFactor("workType")}>
                Type: {getWorkTypeDisplayText()}
              </FilterFactor>
            )}
      </FilterFactorContainer>
      )}
      <FilterCaption onClick={handleFilter}>
      {isMobile && hasActiveFilters && (
          <FilterApplyingSummary onClick={handleFilter}>
            {getModalMessage('FILTER_APPLYING')} ·
          </FilterApplyingSummary>
        )}
        <IconBtn $backgroundImg={filterIcon} />
          Filter
      </FilterCaption>
    </FilterStationComp >
  );
}

const FilterStationComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : 'fit-content'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  height: ${({ $isActive }) => ($isActive ? '0%' : 'calc(2vh);')};

  display: flex;
  align-items: center;

  gap: 8px;
  transition: all 0.7s ease-in-out;
  overflow: hidden;
`;

const FilterCaption = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  gap: 4px;
`;

const FilterFactorContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-right: 8px;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const FilterFactor = styled.div`
  width: fit-content;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.color_Gray_05};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
`;

const FilterApplyingSummary = styled.span`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  user-select: none;
  cursor: pointer;
  white-space: nowrap;
`;

export default FilterStation;


const IconBtn = styled.div<{ $backgroundImg: string }>`
  width: clamp(10px, 2vw, 12px);
  height: clamp(10px, 2vw, 12px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;
